/**
 * FieldOptionsNotice — what a field says when the server has not told it what
 * it may hold.
 *
 * Reviews B2cT2-R-02 / BF-03. `GET /users/field-options` is the ONE source of
 * the employment-type options (principles.md #3), so when it is in flight or
 * has failed there is nothing legitimate to render in its place. The defect
 * was not the empty control — that is the fail-closed direction and stays —
 * it was the SILENCE: the create wizard's step 1 requires an employment type,
 * the RadioGroup had no radios to pick, Next was disabled forever, and no
 * spinner, message or toast said why. An admin could not create any user and
 * was told nothing.
 *
 * ONE set of words for all three screens (the create wizard, the edit dialog
 * and the roster filter), so a fourth reader cannot invent a fourth sentence.
 * It renders nothing at all once the options are in hand.
 */

import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface FieldOptionsNoticeProps {
  /** `options.isAvailable` from `useUserFieldOptions()`. */
  isAvailable: boolean;
  /** `isLoading` from the same hook. */
  isLoading: boolean;
  /** `error` from the same hook. */
  error: unknown;
  /** `refetch` from the same hook. */
  onRetry: () => void;
  /** What the operator was trying to choose, for the sentence. */
  fieldLabel?: string;
}

export function FieldOptionsNotice({
  isAvailable,
  isLoading,
  error,
  onRetry,
  fieldLabel = 'Employment type',
}: FieldOptionsNoticeProps) {
  if (isAvailable) return null;

  if (isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading {fieldLabel.toLowerCase()} options…
      </p>
    );
  }

  // Not loading and still nothing: the request failed, or answered nothing.
  // Either way the operator is stuck until it succeeds, so say so and give
  // them the one action that can change it.
  return (
    <div
      role="alert"
      className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-2.5 py-2"
    >
      <p className="text-[11px] text-amber-700 dark:text-amber-400 flex-1">
        {fieldLabel} options could not be loaded, so there is nothing to choose
        from{error ? '' : ' yet'}. The list comes from the server — retry, or
        reload the page.
      </p>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 text-[11px]"
        onClick={onRetry}
      >
        <RefreshCw className="h-3 w-3 mr-1.5" /> Retry
      </Button>
    </div>
  );
}
